/**
 * Pure helpers for the pipeline-runs super-admin view.
 */

import type { IStepRunStatus } from "../../../../components/PipelineGraph/interfaces"
import type { IPipelineRunStep, TPipelineRunStatus } from "../../../../lib/api/pipeline-runs"

/** Detail poll cadence while a selected run is in flight. */
export const DETAIL_POLL_MS = 2_000

/** List poll cadence while any listed run is in flight. */
export const LIST_POLL_MS = 4_000

/** True when a run should be polled (still executing). */
export function isRunLive(status: TPipelineRunStatus | string | undefined | null): boolean {
  return status === "running"
}

/**
 * True when the list query should keep polling — any row still `running`.
 * Settled lists return false so we don't churn the network.
 */
export function hasAnyRunningRun(runs: ReadonlyArray<{ status: string }> | undefined | null): boolean {
  return Boolean(runs?.some((r) => r.status === "running"))
}

/**
 * Build the PipelineGraph `runStatus` map from latest-per-node steps.
 *
 * Bottleneck heuristic (v1): the step with the maximum non-null durationMs
 * is flagged. When several share the same max, the first in array order wins.
 * Phase 5 will replace this with p50/p95 baselining against historical runs.
 */
export function buildRunStatusMap(steps: readonly IPipelineRunStep[]): Record<string, IStepRunStatus> {
  let bottleneckNodeId: string | null = null
  let maxDuration = -1
  for (const step of steps) {
    if (step.durationMs !== null && step.durationMs > maxDuration) {
      maxDuration = step.durationMs
      bottleneckNodeId = step.nodeId
    }
  }

  const map: Record<string, IStepRunStatus> = {}
  for (const step of steps) {
    map[step.nodeId] = {
      status: step.status,
      durationMs: step.durationMs,
      error: step.error,
      isBottleneck: bottleneckNodeId !== null && step.nodeId === bottleneckNodeId,
    }
  }
  return map
}

/**
 * Format a run or step duration for table cells.
 * Examples: `4.1s`, `12.0s`, `—`.
 */
export function formatDurationMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return "—"
  if (ms < 1000) return `${Math.round(ms)}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

/**
 * Compact WHEN column for the runs table. Pure Intl — no Lingui / no new
 * translatable strings. Uses a fixed en-GB pattern so output is stable across
 * runtimes (Storybook vs WebKit) and short enough for a dense admin table.
 * Examples: `7 Aug 2026, 14:32`.
 */
export function formatRunWhen(value: string | null | undefined): string {
  if (!value) return "—"
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d)
}

/** Group steps by wave (ascending) for the no-graph fallback list. */
export function groupStepsByWave(steps: readonly IPipelineRunStep[]): Array<{
  wave: number
  steps: IPipelineRunStep[]
}> {
  const byWave = new Map<number, IPipelineRunStep[]>()
  for (const step of steps) {
    const bucket = byWave.get(step.wave)
    if (bucket) {
      bucket.push(step)
    } else {
      byWave.set(step.wave, [step])
    }
  }
  return [...byWave.entries()].sort(([a], [b]) => a - b).map(([wave, waveSteps]) => ({ wave, steps: waveSteps }))
}
